/**
 * Client-side image processor for admin artwork uploads.
 *
 * - Resizes to a max edge of 1600px (preserves aspect ratio)
 * - Applies a tiled diagonal "ART BY SUSAN DENNY" watermark at ~30% opacity
 * - Re-encodes as JPEG at quality 0.9 to keep upload payload small
 *
 * No server-side processing; happens entirely in the browser before upload.
 */

const DEFAULTS = {
  maxDimension: 1600,
  watermarkText: "ART BY SUSAN DENNY",
  opacity: 0.3, // mid of the 0.2–0.4 range
  jpegQuality: 0.9,
};

export async function processArtworkImage(file, opts = {}) {
  const cfg = { ...DEFAULTS, ...opts };
  const img = await loadImage(file);

  // Preserve aspect ratio, scale down only (never up)
  let { width: w, height: h } = img;
  const longest = Math.max(w, h);
  if (longest > cfg.maxDimension) {
    const scale = cfg.maxDimension / longest;
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);

  if (cfg.watermarkText) {
    drawDiagonalWatermark(ctx, w, h, cfg.watermarkText, cfg.opacity);
  }

  return canvas.toDataURL("image/jpeg", cfg.jpegQuality);
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Image could not be decoded."));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("File could not be read."));
    reader.readAsDataURL(file);
  });
}

function drawDiagonalWatermark(ctx, w, h, text, opacity) {
  ctx.save();
  // Font scales with image — readable on small thumbnails, not overpowering on large
  const fontSize = Math.max(14, Math.round(w / 55));
  ctx.font = `300 ${fontSize}px Georgia, "Cormorant Garamond", serif`;

  // Letter-spaced uppercase via manual draw — Canvas doesn't support CSS letter-spacing
  const spacedText = text.split("").join("\u2009"); // thin space between letters

  // Soft white fill with subtle dark outline so it reads on both pale and dark passages
  ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
  ctx.strokeStyle = `rgba(0, 0, 0, ${opacity * 0.45})`;
  ctx.lineWidth = Math.max(1, fontSize / 22);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Rotate -22deg around centre, then tile a grid covering the rotated bounds
  ctx.translate(w / 2, h / 2);
  ctx.rotate((-22 * Math.PI) / 180);
  ctx.translate(-w / 2, -h / 2);

  const stepX = Math.max(fontSize * 18, 280);
  const stepY = Math.max(fontSize * 7, 110);
  const diag = Math.ceil(Math.sqrt(w * w + h * h));

  for (let y = -diag; y < diag * 2; y += stepY) {
    // Stagger every other row by half a step
    const offsetX = ((Math.round(y / stepY) % 2) * stepX) / 2;
    for (let x = -diag + offsetX; x < diag * 2; x += stepX) {
      ctx.strokeText(spacedText, x, y);
      ctx.fillText(spacedText, x, y);
    }
  }
  ctx.restore();
}

/** Pretty file-size summary for the admin preview, e.g. "1.2 MB" */
export function dataUrlSizeKB(dataUrl) {
  // base64 expands by ~4/3; (length * 3/4) is the raw byte count
  if (!dataUrl?.startsWith("data:")) return null;
  const b64 = dataUrl.split(",")[1] || "";
  return Math.round((b64.length * 3) / 4 / 1024);
}
